import Tooltip from '@components/tooltip/tooltip';
import { useFormContext } from 'react-hook-form';
import { Label, Select, TextInput } from '@trussworks/react-uswds';
import classnames from 'classnames';
import { useMemo } from 'react';
import FileDownloadList from '../file-download-list/FileDownloadList';
import { formatReadOnlyValue } from '@src/utils/helpers';

import './selectInput.scss';

const SelectInput = ({
  children,
  className,
  defaultOption = '-Select Option-',
  fileDownload,
  defaultValue,
  fullWidth = true,
  group,
  showOptionalText = true,
  label,
  name,
  onBlur,
  onChange,
  options = [],
  readOnly,
  required,
  tooltip,
  tooltipClickable,
  validations,
  ...customProps
}) => {
  const {
    register,
    formState: { errors },
    trigger,
    getValues,
  } = useFormContext();

  const inputError = errors[name];

  const containerStyles = classnames('select-container', className, {
    'is-invalid': inputError,
    'width-full': fullWidth,
  });
  const labelStyles = classnames({
    required: required,
    optional: !required && showOptionalText,
  });

  const handleOnChange = async (e) => {
    await trigger(name);
    onChange?.(e);
  };

  const {
    ref: selectRef,
    defaultValue: rhfDefault,
    ...rest
  } = register(name, {
    onBlur,
    onChange: handleOnChange,
    ...validations,
  });

  const inputDefault = defaultValue || rhfDefault;

  const renderedOptions = useMemo(() => {
    const allHaveSortOrder = options.every((option) => option.hasOwnProperty('sortOrder'));
    const sortedOptions = allHaveSortOrder ? options.sort((a, b) => a.sortOrder - b.sortOrder) : options;

    return sortedOptions.map((option) => (
      <option key={option.value} value={option.value ? option.value : option}>
        {option.label ? option.label : option}
      </option>
    ));
  }, [options]);

  const readOnlyValue = useMemo(() => {
    const formattedValue = formatReadOnlyValue(getValues(name));

    if (options.length > 0) {
      return options.find((option) => option?.value === formattedValue)?.label;
    } else if (formattedValue) {
      return formattedValue;
    } else {
      return inputDefault;
    }
  }, [options, getValues, name, inputDefault]);

  return (
    <>
      <Label className={labelStyles} htmlFor={name}>
        <span id={`${name}_label`}>{label}</span>
        {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
      </Label>
      {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
      {readOnly ? (
        <div>
          <TextInput className='margin-bottom-2' id={name} name={name} readOnly={readOnly} value={readOnlyValue} />
        </div>
      ) : (
        <div className={containerStyles}>
          <Select
            aria-invalid={inputError ? 'true' : 'false'}
            aria-required={required ? 'true' : 'false'}
            defaultValue={inputDefault}
            disabled={readOnly}
            id={name}
            inputRef={selectRef}
            name={name}
            required={required}
            {...rest}
            {...customProps}
          >
            <option
              value=''
              style={{ display: required && showOptionalText ? 'none' : 'inline' }}
            >{`${defaultOption}`}</option>
            {!group && renderedOptions}
            {group && <optgroup label={group}>{renderedOptions}</optgroup>}
            {children}
          </Select>
        </div>
      )}
    </>
  );
};

export default SelectInput;
